import type { FC } from 'react';
import { useState, useEffect, useRef } from 'react';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';

import type { IAnalyticsSender, TMappedFields, TFields } from './models';
import { useCachedValue, isValidFioFields, onBlurFieldAnalytics } from './helpers';

export const Sender: FC<IAnalyticsSender> = ({
    active,
    analytics,
    dirty,
    fields,
    paymentSystem,
    resident,
    values,
}) => {
    /**
     * кэширование значений для последующих сравнений (prevState vs currentState)
     * */
    const [cachedFields, setCachedFields] = useCachedValue<TMappedFields>(fields);
    const [cachedActive, setCachedActive] = useCachedValue<TFields>(active);

    const [firstDirtySended, setFirstDirtySended] = useState(false);

    const isFirstRender = useRef(true);

    /**
     * аналитика на первое взаимодействие пользователя с формой
     * */
    useEffect(() => {
        if (!firstDirtySended && dirty) {
            setFirstDirtySended(true);
            sendEvent(analytics.firstInteraction);
        }
    }, [dirty, firstDirtySended, analytics.firstInteraction]);

    useEffect(() => {
        const fioFieldNames: Record<string, string> = {
            lastName: 'Фамилия',
            firstName: 'Имя',
            middleName: 'Отчество',
            sex: 'Пол',
        };

        if (cachedFields.fullName && !fields.fullName) {
            const errorFields = Object.entries(fields)
                .map(([name, field]) => {
                    if (fioFieldNames[name] && field.error) {
                        return fioFieldNames[name];
                    }
                })
                .filter(Boolean);

            if (analytics?.fullName?.expandToThreeFields) {
                sendEvent({
                    ...analytics?.fullName?.expandToThreeFields,
                    label_detail: errorFields.join(', '),
                });
            }

            if (analytics?.sex?.view) {
                sendEvent(analytics.sex.view);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cachedFields.fullName, fields.fullName]);

    /**
     * аналитика на "фокус"
     * */
    useEffect(() => {
        /**
         * при переключении между полями `active` становится `undefined`
         * отправка только при `active` соответсвующем полю формы и наличии аналитики на это поле
         * */
        if (active && analytics[active]) {
            sendEvent(analytics[active]?.focus);
            /**
             * запоминаем значение `active`, для использования в аналитике на "блюр"
             * */
            setCachedActive(active);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    /**
     * аналитика на "блюр"
     * слушает изменения объекта `fields`, который меняется только при переключении фокуса
     * */
    useEffect(() => {
        const activeField = fields[cachedActive];
        const cachedActiveField = cachedFields[cachedActive];

        /**
         * при первом рендере формы объект с полями пустой, поэтому проверяем наличие поля
         * */
        if (activeField) {
            onBlurFieldAnalytics({
                activeField,
                cachedActiveField,
                analytics,
                cachedActive,
                fields,
            });
            /**
             * обновление кэша для следующих проверок
             * */
            setCachedFields({ ...cachedFields, [cachedActive]: activeField });
        }

        /**
         * Отправляет эвент об успешном заполнении, когда заполнено ФИО в 3 поля
         * */
        if (!fields.fullName && isValidFioFields(fields)) {
            sendEvent(analytics.fullName?.correct);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields, cachedActive]);

    useEffect(() => {
        const withoutMiddleName = fields.middleName?.value === '-';

        if (withoutMiddleName && analytics?.hasMiddleName?.click) {
            sendEvent({ ...analytics.hasMiddleName.click, label_detail: 'Выбрано' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields.middleName?.value]);

    useEffect(() => {
        if (dirty && analytics?.sex?.click) {
            sendEvent({
                ...analytics.sex.click,
                label_detail: values.sex === 'm' ? 'Мужской' : 'Женский',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.sex]);

    useEffect(() => {
        if (dirty && values.isClient && analytics?.isClient?.click) {
            sendEvent({
                ...analytics.isClient.click,
                label_detail: values.isClient === 'client' ? 'Да' : 'Нет',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.isClient]);

    /**
     * аналитика на переключение тэгов клиент/не клиент
     * */
    useEffect(() => {
        if (values.isClient && !dirty && analytics?.isClient?.view) {
            sendEvent({
                ...analytics.isClient.view,
                label_detail: values.isClient === 'client' ? 'Да' : 'Нет',
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.isClient]);

    /**
     * аналитика на переключение radio-button гражданства
     * */
    useEffect(() => {
        if (!isFirstRender.current) {
            sendEvent({
                ...analytics.resident,
                label_detail: resident === 'resident' ? 'Да' : 'Нет',
            });

            return;
        }
        sendEvent(analytics.residentView);

        // Устанавливаем ref в false после первой отрисовки
        isFirstRender.current = false;
    }, [resident, analytics]);

    /**
     * аналитика на переключение radio-button платежной системы
     * */
    useEffect(() => {
        sendEvent(analytics.paymentSystem);
    }, [paymentSystem, analytics]);

    return null;
};
